<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
    <div v-show="visible">
        <v-icon
            large
            v-show="!heightmapVisible"
            class="heightmapshowicon"
            @click="toggleHeightmap()"
        >mdi-map-legend
        </v-icon>
        <div
            :class="heightmapStyle"
            v-show="heightmapVisible"
        >
            <v-icon
                class="heightmaphideicon"
                @click="toggleHeightmap()"
            >mdi-chevron-down</v-icon>
            <v-card>
                <v-card-title class="py-6">
                    <div class="text-h4">{{ language.t('RoadProfiler.Heightmap.title') }}</div>
                </v-card-title>
                <v-card-text
                    style="max-height: 400px; overflow-y: scroll;"
                    v-if="data != null"
                >
                    <v-overlay
                        :value="loading"
                        absolute
                    >
                        <v-progress-circular
                            indeterminate
                            size="64"
                        ></v-progress-circular>
                    </v-overlay>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="9">
                                <v-subheader dense>{{ language.t('RoadProfiler.Heightmap.offset') }}</v-subheader>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    v-model="offset"
                                    @change="updateHeightmap"
                                    type="number"
                                    step="0.1"
                                    hide-details
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="9">
                                <v-subheader dense>{{ language.t('RoadProfiler.Heightmap.minMillingNumber') }}
                                </v-subheader>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    :value="computedMinValue"
                                    type="number"
                                    hide-details
                                    disabled
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="9">
                                <v-subheader dense>{{ language.t('RoadProfiler.Heightmap.maxMillingNumber') }}
                                </v-subheader>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    :value="computedMaxValue"
                                    type="number"
                                    hide-details
                                    disabled
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="9">
                                <v-subheader dense>{{ language.t('RoadProfiler.Heightmap.minColorRange') }}
                                </v-subheader>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    v-model="computedColorMin"
                                    @change="updateHeightmap"
                                    type="number"
                                    step="0.1"
                                    hide-details
                                    dense
                                    prepend-icon="mdi-restore"
                                    @click:prepend="colorMin = roundCm(data.minHeight); updateHeightmap();"
                                >
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="9">
                                <v-subheader dense>{{ language.t('RoadProfiler.Heightmap.maxColorRange') }}
                                </v-subheader>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    v-model="computedColorMax"
                                    @change="updateHeightmap"
                                    type="number"
                                    step="0.1"
                                    hide-details
                                    dense
                                    prepend-icon="mdi-restore"
                                    @click:prepend="colorMax = roundCm(data.maxHeight); updateHeightmap();"
                                >
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script>
export default {
    components: {
    },
    data: () => ({
        visible: false,
        heightmapVisible: false,
        data: null,
        offset: 0,
        colorMin: 0,
        colorMax: 1,
        loading: false
    }),
    props: {
    },
    watch: {},
    methods: {
        toggleHeightmap() {
            this.heightmapVisible = !this.heightmapVisible;

            if (this.heightmapVisible && this.$vuetify.breakpoint.xsOnly) {
                this.$emit("changePopup", "heightmap");
            }
        },
        hideHeightmap() {
            this.heightmapVisible = false;
        },
        setData(data) {
            if (data == null) { this.clear(); return; }
            this.loading = false;
            this.visible = true;
            this.heightmapVisible = !this.$vuetify.breakpoint.xsOnly;
            if (this.data == null) {
                this.colorMin = this.roundCm(data.minHeight);
                this.colorMax = this.roundCm(data.maxHeight);
            }
            this.data = data;
        },
        clear() {
            this.visible = false;
            this.data = null;
            this.offset = 0;
        },
        roundCm(value) {
            return Math.round(value * 100 * 10) / 10;
        },
        updateHeightmap() {
            this.loading = true;
            this.$emit("changed", { colorMin: parseFloat(this.computedColorMin) / 100, colorMax: parseFloat(this.computedColorMax) / 100, offset: parseFloat(this.offset) / 100 });
        }
    },
    computed: {
        heightmapStyle: function () {
            return {
                floating: this.heightmapVisible && !this.$vuetify.breakpoint.xsOnly,
                hidden: !this.heightmapVisible,
                popup: this.heightmapVisible && this.$vuetify.breakpoint.xsOnly
            };
        },
        computedColorMin: {
            get() {
                if ((Number(this.colorMin) + Number(this.offset)) % 1 === 0) {
                    return (Number(this.colorMin) + Number(this.offset))
                } else {
                    return (Number(this.colorMin) + Number(this.offset)).toFixed(1);
                }
            },
            set(newValue) {
                if (!newValue) return;
                this.colorMin = Number(newValue) - Number(this.offset);
            }
        },
        computedColorMax: {
            get() {
                if ((Number(this.colorMax) + Number(this.offset)) % 1 === 0) {
                    return (Number(this.colorMax) + Number(this.offset))
                } else {
                    return (Number(this.colorMax) + Number(this.offset)).toFixed(1);
                }
            },
            set(newValue) {
                if (!newValue) return;
                this.colorMax = Number(newValue) - Number(this.offset);
            }
        },
        computedMinValue: {
            get() {
                return (this.roundCm(this.data.minHeight) + Number(this.offset)).toFixed(1);
            }
        },
        computedMaxValue: {
            get() {
                return (this.roundCm(this.data.maxHeight) + Number(this.offset)).toFixed(1);
            }
        }
    }
};
</script>

<style scoped>
.v-text-field {
    padding-top: 0px;
    margin-top: 0px;
}

.v-text-field>>>input {
    text-align: right;
}

.v-subheader {
    height: 30px;
}

.v-input--is-disabled>>>input {
    color: black !important;
}

.floating {
    z-index: 75;
    position: absolute;
    bottom: 10px;
    right: 10px;
}

.popup {
    z-index: 202;
    position: absolute;
    top: 0px;
    max-height: 80vh;
    width: 100vw !important;
}

.hidden {
    display: none;
}

.heightmaphideicon {
    z-index: 70;
    float: right;
    margin-top: 5px;
}

.heightmapshowicon {
    z-index: 70;
    position: absolute;
    bottom: 10px;
    right: 125px;
}
</style>
